import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { AlertTriangle, Copy, Check, RefreshCw, Loader2, Package, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_META = {
  reschedule:       { label: 'Reagendada',     className: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  return_to_origin: { label: 'Retorno origen', className: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  abandon:          { label: 'Abandonada',     className: 'bg-red-500/15 text-red-300 border-red-500/30' },
};

const ACTION_OPTIONS = [
  { value: 'reschedule', label: 'Reagendar entrega' },
  { value: 'return_to_origin', label: 'Retornar a origen' },
  { value: 'abandon', label: 'Abandonar' },
];

const AUTO_REFRESH_MS = 5 * 60 * 1000;

export default function NDR() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [actionForm, setActionForm] = useState({ action: 'reschedule', notes: '' });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);
  const refreshTimer = useRef(null);

  const fetchCases = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/envia/admin/ndr`, { headers });
      setCases(Array.isArray(r.data) ? r.data : []);
    } catch {
      toast.error('Error cargando NDR');
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchCases();
    refreshTimer.current = setInterval(fetchCases, AUTO_REFRESH_MS);
    return () => { if (refreshTimer.current) clearInterval(refreshTimer.current); };
  }, [fetchCases]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/envia/ndr/sync`, {}, { headers });
      toast.success(`Sincronización completa: ${r.data.synced} órdenes revisadas, ${r.data.updated} actualizadas a "failed"`);
      await fetchCases();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error sincronizando');
    } finally {
      setSyncing(false);
    }
  };

  const handleCopy = async (tracking) => {
    try {
      await navigator.clipboard.writeText(tracking);
      setCopied(tracking);
      toast.success('Tracking copiado');
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const openAction = (c) => {
    setEditItem(c);
    setActionForm({ action: c.ndr_action || 'reschedule', notes: c.ndr_notes || '' });
    setDialogOpen(true);
  };

  const handleSaveAction = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/envia/admin/ndr/${editItem.id}/action`, actionForm, { headers });
      setCases(prev => prev.map(c => c.id === editItem.id ? { ...c, ...r.data } : c));
      toast.success(actionForm.action === 'reschedule' ? 'Acción guardada y email enviado al cliente' : 'Acción guardada');
      setDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando acción');
    } finally {
      setSaving(false);
    }
  };

  // Stats
  const totalFailed = cases.length;
  const pendingAction = cases.filter(c => !c.ndr_action).length;
  const resolved = cases.filter(c => !!c.ndr_action).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-400" /> NDR · Entregas Fallidas
          </h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="ndr-subtitle">
            Non-Delivery Reports. Con los webhooks de Envia.com configurados, los estados de envío
            se actualizan automáticamente en tiempo real. El botón "Sincronizar" sigue disponible
            como respaldo manual.
          </p>
        </div>
        <Button onClick={handleSync} disabled={syncing} className="gap-2" data-testid="ndr-sync-button">
          {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {syncing ? 'Sincronizando...' : 'Sincronizar con Envia'}
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-red-500/30 bg-red-500/5" data-testid="ndr-stat-total">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-md bg-red-500/15"><AlertTriangle className="h-5 w-5 text-red-300" /></div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Entregas Fallidas</p>
              <p className="text-3xl font-display font-bold text-red-300 tabular-nums">{totalFailed}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-yellow-500/30 bg-yellow-500/5" data-testid="ndr-stat-pending">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-md bg-yellow-500/15"><Package className="h-5 w-5 text-yellow-300" /></div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Pendientes de acción</p>
              <p className="text-3xl font-display font-bold text-yellow-300 tabular-nums">{pendingAction}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-500/30 bg-green-500/5" data-testid="ndr-stat-resolved">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-md bg-green-500/15"><CheckCircle2 className="h-5 w-5 text-green-300" /></div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Acción tomada</p>
              <p className="text-3xl font-display font-bold text-green-300 tabular-nums">{resolved}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      {loading ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground">Cargando...</CardContent></Card>
      ) : cases.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle2 className="h-12 w-12 text-green-400/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No hay entregas fallidas registradas</p>
            <p className="text-xs text-muted-foreground mt-1">Presiona "Sincronizar con Envia" para revisar manualmente.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table data-testid="ndr-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Pedido</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Paquetería</TableHead>
                  <TableHead>Tracking</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Acción</TableHead>
                  <TableHead className="text-right">Gestión</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cases.map(c => {
                  const meta = c.ndr_action ? ACTION_META[c.ndr_action] : null;
                  return (
                    <TableRow key={c.id} data-testid={`ndr-row-${c.order_number}`}>
                      <TableCell className="font-mono text-xs font-medium">{c.order_number}</TableCell>
                      <TableCell>
                        <div className="text-sm">{c.user_name || '—'}</div>
                        <div className="text-xs text-muted-foreground">{c.user_email}</div>
                      </TableCell>
                      <TableCell className="text-sm">{c.envia_carrier?.toUpperCase() || '—'}</TableCell>
                      <TableCell>
                        {c.envia_tracking_number ? (
                          <div className="flex items-center gap-1.5">
                            <code className="text-xs font-mono">{c.envia_tracking_number}</code>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleCopy(c.envia_tracking_number)} data-testid={`copy-tracking-${c.order_number}`}>
                              {copied === c.envia_tracking_number ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                            </Button>
                          </div>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[180px] truncate" title={c.ndr_notes}>
                        {c.ndr_notes || 'Entrega no completada'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(c.updated_at || c.created_at).toLocaleDateString('es-MX')}
                      </TableCell>
                      <TableCell>
                        {meta ? (
                          <Badge variant="outline" className={meta.className} data-testid={`ndr-action-badge-${c.order_number}`}>{meta.label}</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-yellow-500/15 text-yellow-300 border-yellow-500/30" data-testid={`ndr-action-badge-${c.order_number}`}>Pendiente</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => openAction(c)} data-testid={`ndr-edit-${c.order_number}`}>
                          {c.ndr_action ? 'Editar' : 'Tomar acción'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Action dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="ndr-action-dialog">
          <DialogHeader>
            <DialogTitle>Acción sobre entrega fallida</DialogTitle>
            <DialogDescription>
              {editItem ? `Pedido ${editItem.order_number} · ${editItem.user_email}` : ''}
            </DialogDescription>
          </DialogHeader>
          {editItem && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Acción a tomar *</Label>
                <Select value={actionForm.action} onValueChange={v => setActionForm({ ...actionForm, action: v })}>
                  <SelectTrigger data-testid="ndr-action-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACTION_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {actionForm.action === 'reschedule' && (
                  <p className="text-[10px] text-muted-foreground">
                    Al guardar se enviará un email al cliente pidiendo confirmar dirección.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="ndr-notes">Notas</Label>
                <textarea
                  id="ndr-notes"
                  value={actionForm.notes}
                  onChange={e => setActionForm({ ...actionForm, notes: e.target.value })}
                  rows={4}
                  maxLength={1000}
                  placeholder="Instrucciones, detalle del incidente, comentarios internos..."
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="ndr-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleSaveAction} disabled={saving} data-testid="ndr-confirm-button">
              {saving ? 'Guardando...' : 'Confirmar acción'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
